// Fetches and caches the binary tiles written by
// adaptive_mesh_tile_chunker.py. Geometry (positions + indices) is
// fetched once per tile and reused across timesteps; only the small
// scalar_t<k>.bin buffer is re-fetched when the timestep changes.
import {expandVertexAttribute, splitAntimeridianGeometry} from './lon-unwrap.js';
import {analyzeSeamTile, isSeamDebugEnabled, logSeamSplitStats} from './seam-debug.js';
import {collectVisibleLeaves, collectVisibleLeavesGlobe} from './quadtree.js';
import {fetchGeometryArrayBuffer, invalidateGeometryCache} from './geometry-cache.js';
import {tileDiag, diagNow} from './tile-load-diagnostics.js';

export const FILL_VALUE = -99999.0;

export class TileStore {
  constructor(baseUrl, index) {
    this.baseUrl = baseUrl;
    this.index = index;
    this.cache = new Map(); // "z,x,y" -> tile record
    this._geomRetryKeys = new Set();
  }

  static keyFor(z, x, y) {
    return `${z},${x},${y}`;
  }

  get(z, x, y) {
    return this.cache.get(TileStore.keyFor(z, x, y));
  }

  getVisibleLeaves(viewportBBox, lod = {}) {
    return collectVisibleLeaves(this.index, viewportBBox, lod);
  }

  // GlobeView culling -- see the comment block above
  // collectVisibleLeavesGlobe() in quadtree.js for why this needs a
  // separate 3D-based test instead of a lon/lat bbox, and why minDot is
  // computed fresh per-viewport by the caller rather than a constant.
  getVisibleLeavesGlobe(cameraLon, cameraLat, minDot, screenBBox = null, lod = {}) {
    return collectVisibleLeavesGlobe(
      this.index,
      cameraLon,
      cameraLat,
      minDot,
      screenBBox,
      lod
    );
  }

  async _loadGeometry(z, x, y, entry, {bypassCache = false} = {}) {
    const url = `${this.baseUrl}/${z}/${x}/${y}/geometry.bin`;
    entry.geometryUrl = url;
    const {buffer: buf, source, fetchMs, bytes} = await fetchGeometryArrayBuffer(url, {
      bypassCache
    });
    const parseT0 = diagNow();
    const dv = new DataView(buf);
    const nVerts = dv.getUint32(0, true);
    const nTris = dv.getUint32(4, true);

    const positions2 = new Float32Array(nVerts * 2);
    positions2.set(new Float32Array(buf, 8, nVerts * 2));
    const rawIndices = new Uint32Array(buf, 8 + nVerts * 2 * 4, nTris * 3);
    const split = splitAntimeridianGeometry(positions2, rawIndices);
    const {positions2: splitPos2, indices, vertexSources, splitStats} = split;

    const renderVerts = splitPos2.length / 2;
    const positions3 = new Float32Array(renderVerts * 3);
    for (let i = 0; i < renderVerts; i++) {
      positions3[i * 3] = splitPos2[i * 2];
      positions3[i * 3 + 1] = splitPos2[i * 2 + 1];
      positions3[i * 3 + 2] = 0;
    }

    entry.nVertsOriginal = nVerts;
    entry.nVerts = renderVerts;
    entry.nTris = indices.length / 3;
    entry.vertexSources = vertexSources;
    entry.seamSplitStats = splitStats;
    entry.positions3 = positions3;
    entry.indices = indices;
    entry.status = 'geometry-ready';

    tileDiag.geometry({
      z,
      x,
      y,
      source,
      fetchMs,
      parseMs: diagNow() - parseT0,
      bytes,
      nVerts: renderVerts,
      nTris: entry.nTris
    });

    if (splitStats) {
      logSeamSplitStats(z, x, y, splitStats);
    }
  }

  /**
   * Drop in-memory + Cache Storage geometry (stale after overview remesh).
   */
  async invalidateGeometry(z, x, y) {
    const key = TileStore.keyFor(z, x, y);
    const entry = this.cache.get(key);
    const url = entry?.geometryUrl || `${this.baseUrl}/${z}/${x}/${y}/geometry.bin`;
    this.cache.delete(key);
    await invalidateGeometryCache(url);
  }

  async ensureScalar(z, x, y, timestep) {
    const key = TileStore.keyFor(z, x, y);
    const entry = this.cache.get(key);
    if (!entry || entry.status !== 'geometry-ready') {
      throw new Error(`geometry not loaded for tile ${key}`);
    }
    if (!entry.scalarPromises) entry.scalarPromises = new Map();
    if (!entry.scalarPromises.has(timestep)) {
      entry.scalarPromises.set(
        timestep,
        this._loadScalar(z, x, y, timestep, entry)
          .catch(async err => {
            entry.scalarPromises.delete(timestep);
            const msg = String(err?.message || err);
            const retryKey = `${key}:t${timestep}`;
            // Stale Cache API / immutable HTTP geometry after overview remesh.
            if (
              msg.includes('scalar vertex count mismatch') &&
              !this._geomRetryKeys.has(retryKey)
            ) {
              this._geomRetryKeys.add(retryKey);
              console.warn(
                'stale geometry cache suspected; refetching geometry',
                `${z}/${x}/${y}`
              );
              await this.invalidateGeometry(z, x, y);
              await this.ensureGeometry(z, x, y, {bypassCache: true});
              return this.ensureScalar(z, x, y, timestep);
            }
            throw err;
          })
      );
    }
    return entry.scalarPromises.get(timestep);
  }

  async ensureGeometry(z, x, y, {bypassCache = false} = {}) {
    const key = TileStore.keyFor(z, x, y);
    let entry = this.cache.get(key);
    if (!entry || bypassCache) {
      entry = {status: 'loading', scalarByTimestep: new Map()};
      entry.geometryPromise = this._loadGeometry(z, x, y, entry, {bypassCache});
      this.cache.set(key, entry);
    }
    try {
      await entry.geometryPromise;
    } catch (err) {
      entry.geometryPromise = null;
      this.cache.delete(key);
      throw err;
    }
    return entry;
  }

  async _loadScalar(z, x, y, timestep, entry) {
    const url = `${this.baseUrl}/${z}/${x}/${y}/scalar_t${timestep}.bin`;
    const t0 = diagNow();
    const res = await fetch(url, {cache: 'no-cache'});
    if (!res.ok) throw new Error(`scalar fetch failed: ${url} -> ${res.status}`);
    const buf = await res.arrayBuffer();
    const scalarRaw = new Float32Array(buf);
    if (entry.nVertsOriginal && scalarRaw.length !== entry.nVertsOriginal) {
      throw new Error(
        `scalar vertex count mismatch for ${z}/${x}/${y}: expected ${entry.nVertsOriginal}, got ${scalarRaw.length}`
      );
    }
    const scalar = expandVertexAttribute(scalarRaw, entry.vertexSources, 1);
    const wetMask = new Float32Array(scalar.length);
    for (let i = 0; i < scalar.length; i++) {
      wetMask[i] = scalar[i] === FILL_VALUE ? 0.0 : 1.0;
    }

    const data = {scalar, wetMask};
    entry.scalarByTimestep.set(timestep, data);

    tileDiag.field({
      z,
      x,
      y,
      kind: `scalar_t${timestep}`,
      ms: diagNow() - t0,
      bytes: buf.byteLength,
      cached: false
    });

    if (entry.vertexSources && isSeamDebugEnabled()) {
      analyzeSeamTile(z, x, y, entry, scalar, wetMask);
    }
    return data;
  }

  // field: 'velocity' | 'wind' -- fetches <field>_t<k>.bin, an
  // interleaved Float32Array [u0, v0, u1, v1, ...] in the same vertex
  // order as geometry.bin. Only fetched when either the particle
  // animation or the "Water Speed"/"Wind Speed" fill display needs it.
  async ensureVector(z, x, y, timestep, field) {
    const key = TileStore.keyFor(z, x, y);
    const entry = this.cache.get(key);
    if (!entry || entry.status !== 'geometry-ready') {
      throw new Error(`geometry not loaded for tile ${key}`);
    }
    const byTimestepKey = `${field}ByTimestep`;
    const promisesKey = `${field}Promises`;
    if (!entry[byTimestepKey]) entry[byTimestepKey] = new Map();
    if (!entry[promisesKey]) entry[promisesKey] = new Map();
    if (!entry[promisesKey].has(timestep)) {
      entry[promisesKey].set(
        timestep,
        this._loadVector(z, x, y, timestep, field, entry).catch(err => {
          entry[promisesKey].delete(timestep);
          throw err;
        })
      );
    }
    return entry[promisesKey].get(timestep);
  }

  async _loadVector(z, x, y, timestep, field, entry) {
    const url = `${this.baseUrl}/${z}/${x}/${y}/${field}_t${timestep}.bin`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`vector fetch failed: ${url} -> ${res.status}`);
    const buf = await res.arrayBuffer();
    const uvRaw = new Float32Array(buf); // interleaved [u0, v0, u1, v1, ...]
    const uv = expandVertexAttribute(uvRaw, entry.vertexSources, 2);
    entry[`${field}ByTimestep`].set(timestep, uv);
    return uv;
  }

  // Derives a {scalar, wetMask} pair (same shape ensureScalar returns,
  // so the fill layer can treat "Water Speed"/"Wind Speed" the same way
  // it treats "Water Level") by computing per-vertex magnitude from the
  // field's u/v components. Computed once per tile/timestep and cached
  // -- not recomputed on every render.
  async ensureSpeed(z, x, y, timestep, field) {
    const uv = await this.ensureVector(z, x, y, timestep, field);
    const key = TileStore.keyFor(z, x, y);
    const entry = this.cache.get(key);
    const cacheKey = `${field}SpeedByTimestep`;
    if (!entry[cacheKey]) entry[cacheKey] = new Map();
    if (entry[cacheKey].has(timestep)) return entry[cacheKey].get(timestep);

    const n = uv.length / 2;
    const scalar = new Float32Array(n);
    const wetMask = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const u = uv[i * 2];
      const v = uv[i * 2 + 1];
      if (u === FILL_VALUE || v === FILL_VALUE) {
        scalar[i] = FILL_VALUE;
        wetMask[i] = 0;
      } else {
        scalar[i] = Math.sqrt(u * u + v * v);
        wetMask[i] = 1;
      }
    }

    const data = {scalar, wetMask};
    entry[cacheKey].set(timestep, data);
    return data;
  }

  // Per-triangle circumcircle diameter (km), averaged onto shared vertices.
  // Mesh topology never changes, so this is computed once per tile — typically
  // a few ms even for large coastal tiles (local equirectangular sides + Heron).
  async ensureElementSize(z, x, y) {
    await this.ensureGeometry(z, x, y);
    const entry = this.get(z, x, y);
    if (entry.elementSize) return entry.elementSize;
    if (!entry.elementSizePromise) {
      entry.elementSizePromise = Promise.resolve()
        .then(() => {
          entry.elementSize = computeElementSizeField(entry.positions3, entry.indices, entry.nVerts);
          return entry.elementSize;
        })
        .catch(err => {
          entry.elementSizePromise = null;
          throw err;
        });
    }
    return entry.elementSizePromise;
  }

  // Static bathymetry (distance below geoid, m) from depth.bin. Written
  // once per tile by the chunker (not per timestep), so fetch + cache it a
  // single time and reuse across every timestep, just like geometry.
  async ensureDepth(z, x, y) {
    await this.ensureGeometry(z, x, y);
    const entry = this.get(z, x, y);
    if (entry.depth) return entry.depth;
    if (!entry.depthPromise) {
      entry.depthPromise = this._loadDepth(z, x, y, entry).catch(err => {
        entry.depthPromise = null;
        throw err;
      });
    }
    return entry.depthPromise;
  }

  async _loadDepth(z, x, y, entry) {
    const url = `${this.baseUrl}/${z}/${x}/${y}/depth.bin`;
    const t0 = diagNow();
    const res = await fetch(url, {cache: 'no-cache'});
    if (!res.ok) throw new Error(`depth fetch failed: ${url} -> ${res.status}`);
    const buf = await res.arrayBuffer();
    const depthRaw = new Float32Array(buf);
    if (entry.nVertsOriginal && depthRaw.length !== entry.nVertsOriginal) {
      throw new Error(
        `depth vertex count mismatch for ${z}/${x}/${y}: expected ${entry.nVertsOriginal}, got ${depthRaw.length}`
      );
    }
    const scalar = expandVertexAttribute(depthRaw, entry.vertexSources, 1);
    const wetMask = new Float32Array(scalar.length);
    for (let i = 0; i < scalar.length; i++) {
      wetMask[i] = scalar[i] === FILL_VALUE ? 0.0 : 1.0;
    }
    const data = {scalar, wetMask};
    entry.depth = data;
    tileDiag.field({z, x, y, kind: 'depth', ms: diagNow() - t0, bytes: buf.byteLength, cached: false});
    return data;
  }

  // Per-triangle equilateral quality q = 4√3·Area/(a²+b²+c²) ∈ (0,1],
  // averaged onto shared vertices. 1 = equilateral; →0 as the triangle
  // collapses. Size-independent complement to ensureElementSize().
  async ensureElementShape(z, x, y) {
    await this.ensureGeometry(z, x, y);
    const entry = this.get(z, x, y);
    if (entry.elementShape) return entry.elementShape;
    if (!entry.elementShapePromise) {
      entry.elementShapePromise = Promise.resolve()
        .then(() => {
          entry.elementShape = computeElementShapeField(entry.positions3, entry.indices, entry.nVerts);
          return entry.elementShape;
        })
        .catch(err => {
          entry.elementShapePromise = null;
          throw err;
        });
    }
    return entry.elementShapePromise;
  }
}

// Circumdiameter of triangle with side lengths a,b,c: D = abc / (2 * area).
function circumdiameterFromSides(a, b, c) {
  const s = 0.5 * (a + b + c);
  const areaSq = s * (s - a) * (s - b) * (s - c);
  if (!(areaSq > 0)) return NaN;
  const area = Math.sqrt(areaSq);
  return (a * b * c) / (2 * area);
}

// Equilateral quality in (0, 1]: 1 for equilateral, near 0 for needles/slivers.
function equilateralQualityFromSides(a, b, c) {
  const s = 0.5 * (a + b + c);
  const areaSq = s * (s - a) * (s - b) * (s - c);
  if (!(areaSq > 0)) return NaN;
  const area = Math.sqrt(areaSq);
  const denom = a * a + b * b + c * c;
  if (!(denom > 0)) return NaN;
  // Use Math.sqrt(3) — Math.SQRT3 is missing in some JS environments.
  return (4 * Math.sqrt(3) * area) / denom;
}

function forEachTriangleMetric(positions3, indices, nVerts, metricFn) {
  const sum = new Float64Array(nVerts);
  const count = new Uint32Array(nVerts);
  const nTris = indices.length / 3;

  for (let t = 0; t < nTris; t++) {
    const i0 = indices[t * 3];
    const i1 = indices[t * 3 + 1];
    const i2 = indices[t * 3 + 2];
    const lon0 = positions3[i0 * 3];
    const lat0 = positions3[i0 * 3 + 1];
    const lon1 = positions3[i1 * 3];
    const lat1 = positions3[i1 * 3 + 1];
    const lon2 = positions3[i2 * 3];
    const lat2 = positions3[i2 * 3 + 1];

    // Local tangent meters (plenty for ADCIRC element scales; avoids 3 haversines).
    const latMidRad = ((lat0 + lat1 + lat2) / 3) * (Math.PI / 180);
    const mx = 111320 * Math.cos(latMidRad);
    const my = 110540;
    const x0 = lon0 * mx;
    const y0 = lat0 * my;
    const x1 = lon1 * mx;
    const y1 = lat1 * my;
    const x2 = lon2 * mx;
    const y2 = lat2 * my;
    const a = Math.hypot(x1 - x2, y1 - y2);
    const b = Math.hypot(x0 - x2, y0 - y2);
    const c = Math.hypot(x0 - x1, y0 - y1);
    const value = metricFn(a, b, c);
    if (!(value > 0) || !Number.isFinite(value)) continue;

    sum[i0] += value;
    sum[i1] += value;
    sum[i2] += value;
    count[i0]++;
    count[i1]++;
    count[i2]++;
  }

  const scalar = new Float32Array(nVerts);
  const wetMask = new Float32Array(nVerts);
  for (let i = 0; i < nVerts; i++) {
    if (count[i] > 0) {
      scalar[i] = sum[i] / count[i];
      wetMask[i] = 1;
    } else {
      scalar[i] = FILL_VALUE;
      wetMask[i] = 0;
    }
  }
  return {scalar, wetMask};
}

function computeElementSizeField(positions3, indices, nVerts) {
  return forEachTriangleMetric(positions3, indices, nVerts, (a, b, c) => {
    const d = circumdiameterFromSides(a, b, c);
    return d > 0 && Number.isFinite(d) ? d / 1000 : NaN;
  });
}

function computeElementShapeField(positions3, indices, nVerts) {
  return forEachTriangleMetric(positions3, indices, nVerts, equilateralQualityFromSides);
}
